import MoneyInput from '../../components/MoneyInput';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { floridaOwnersTitlePremium } from './titleInsurance';
import { page, wrap, h1, h2, muted, card, grid, label, input, btn, backBtn, row, bigRow, money, num } from './ui';
import { closingCosts } from '../../shared/calculators';
import UnderConstructionBanner from '../../components/UnderConstructionBanner';

// Florida standard amounts. Doc stamp and intangible tax rates are set by Florida law.
// Fee lines marked "estimate" are defaults you can change to match the deal and the title company's quote.
// Rates: deed doc stamps 0.70%, mortgage doc stamps 0.35%, intangible tax 0.2% (src/shared/calculators.js).

const STATE_NAMES = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "District of Columbia",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};

export default function ClosingCostEstimator() {
  const navigate = useNavigate();
  const [side, setSide] = useState('buyer');
  const [state, setState] = useState('FL');
  const [propertyType, setPropertyType] = useState('resale');
  const [ownerTitlePayer, setOwnerTitlePayer] = useState('seller');
  const changePropertyType = (val) => {
    setPropertyType(val);
  };
  const [stateTax, setStateTax] = useState('0');
  const [price, setPrice] = useState('600000');
  const [downPct, setDownPct] = useState('20');

  // Buyer fee defaults (estimates)
  const [lenderTitle, setLenderTitle] = useState('1200');
  const [settlement, setSettlement] = useState('1500');
  const [recording, setRecording] = useState('150');
  const [lenderFees, setLenderFees] = useState('3500');
  const [appraisal, setAppraisal] = useState('600');
  const [prepaids, setPrepaids] = useState('3000');

  // Seller fee defaults (estimates)
  const [commissionPct, setCommissionPct] = useState('3');
  const [sellerSettlement, setSellerSettlement] = useState('1000');
  const [sellerRecording, setSellerRecording] = useState('300');
  const [survey, setSurvey] = useState('400');
  const [payoff, setPayoff] = useState('0');

  const isFL = state === 'FL';
  // Same math as the MCP closing_cost_estimator tool (src/shared/calculators.js).
  const { salePrice, loan, down, buyerLines, buyerClosing, buyerCashToClose, commission, sellerLines, sellerNet } = closingCosts({
    state, ownerTitlePayer, stateTax, price, downPct, lenderTitle, settlement, recording, lenderFees, appraisal, prepaids,
    commissionPct, sellerSettlement, sellerRecording, survey, payoff,
  });

  return (
    <div style={page}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, div { background: #fff !important; }
        }
      `}</style>
      <div style={wrap}>
        <button className="no-print" onClick={() => navigate('/tools')} style={backBtn}>← Back to tools</button>
        <h1 style={h1}>Closing Cost Estimate</h1>
        <div className="no-print"><UnderConstructionBanner /></div>
        <p style={muted}>
          {STATE_NAMES[state]} estimate for {side === 'buyer' ? 'a buyer' : 'a seller'}. {isFL ? 'Doc stamp and intangible tax rates are the Florida state rates.' : 'Only Florida transfer and mortgage tax rates are built in, so enter those amounts manually.'}
          Other lines are estimates: change them to match the deal and confirm final figures with the title company or lender.
        </p>

        <div className="no-print" style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
          {[['buyer', 'Buyer'], ['seller', 'Seller']].map(([key, text]) => (
            <button key={key} onClick={() => setSide(key)} style={{
              ...btn,
              backgroundColor: side === key ? '#1a1a1a' : '#ffffff',
              color: side === key ? '#D4AF37' : '#1a1a1a',
              border: '1px solid #d8c9a8',
            }}>{text}</button>
          ))}
        </div>

        <div className="no-print" style={card}>
          <h2 style={h2}>Deal</h2>
          <div style={grid}>
            <div>
              <label style={label}>State</label>
              <select style={input} value={state} onChange={(e) => setState(e.target.value)}>
                {Object.keys(STATE_NAMES).sort((a, b) => STATE_NAMES[a].localeCompare(STATE_NAMES[b])).map((code) => (
                  <option key={code} value={code}>{STATE_NAMES[code]}</option>
                ))}
              </select>
            </div>
            <div><label style={label}>Sale price</label><MoneyInput style={input} value={price} onChange={(e) => setPrice(e.target.value)} /></div>
            <div><label style={label}>Property type</label><select style={input} value={propertyType} onChange={(e) => changePropertyType(e.target.value)}><option value="resale">Resale</option><option value="new">New construction</option></select></div>
            <div><label style={label}>Owner's title policy paid by</label><select style={input} value={ownerTitlePayer} onChange={(e) => setOwnerTitlePayer(e.target.value)}><option value="seller">Seller</option><option value="buyer">Buyer</option></select></div>
            {!isFL && (
              <div><label style={label}>State transfer and mortgage taxes (enter manually)</label><input style={input} value={stateTax} onChange={(e) => setStateTax(e.target.value)} /></div>
            )}
            {side === 'buyer' && (
              <div><label style={label}>Down payment (%)</label><input style={input} value={downPct} onChange={(e) => setDownPct(e.target.value)} /></div>
            )}
            {side === 'seller' && (
              <>
                <div><label style={label}>Commission (%)</label><input style={input} value={commissionPct} onChange={(e) => setCommissionPct(e.target.value)} /></div>
                <div><label style={label}>Mortgage payoff</label><input style={input} value={payoff} onChange={(e) => setPayoff(e.target.value)} /></div>
              </>
            )}
          </div>
        </div>

        <div className="no-print" style={card}>
          <h2 style={h2}>{side === 'buyer' ? 'Buyer fee estimates' : 'Seller fee estimates'}</h2>
          <div style={grid}>
            {side === 'buyer' ? (
              <>
                <div><label style={label}>Lender's title insurance</label><input style={input} value={lenderTitle} onChange={(e) => setLenderTitle(e.target.value)} /></div>
                <div><label style={label}>Title and settlement</label><input style={input} value={settlement} onChange={(e) => setSettlement(e.target.value)} /></div>
                <div><label style={label}>Recording</label><input style={input} value={recording} onChange={(e) => setRecording(e.target.value)} /></div>
                <div><label style={label}>Lender fees</label><MoneyInput style={input} value={lenderFees} onChange={(e) => setLenderFees(e.target.value)} /></div>
                <div><label style={label}>Appraisal</label><input style={input} value={appraisal} onChange={(e) => setAppraisal(e.target.value)} /></div>
                <div><label style={label}>Prepaid taxes and insurance</label><input style={input} value={prepaids} onChange={(e) => setPrepaids(e.target.value)} /></div>
              </>
            ) : (
              <>
                <div><label style={label}>Owner's title (Florida rate)</label><div style={{ ...input, background: 'transparent' }}>{money(floridaOwnersTitlePremium(salePrice))}</div></div>
                <div><label style={label}>Title and settlement</label><input style={input} value={sellerSettlement} onChange={(e) => setSellerSettlement(e.target.value)} /></div>
                <div><label style={label}>Recording and release</label><input style={input} value={sellerRecording} onChange={(e) => setSellerRecording(e.target.value)} /></div>
                <div><label style={label}>Survey and HOA documents</label><input style={input} value={survey} onChange={(e) => setSurvey(e.target.value)} /></div>
              </>
            )}
          </div>
        </div>

        <div style={card}>
          <h2 style={h2}>Estimate</h2>
          {side === 'buyer' ? (
            <>
              <div style={row}><span>Down payment ({downPct}%)</span><span>{money(down)}</span></div>
              {buyerLines.map((l) => (
                <div key={l.label} style={row}><span>{l.label}</span><span>{money(l.amount)}</span></div>
              ))}
              <div style={row}><span>Total closing costs</span><span>{money(buyerClosing)}</span></div>
              <div style={bigRow}><span>Estimated cash to close</span><span>{money(buyerCashToClose)}</span></div>
              <p style={muted}>Loan amount: {money(loan)}. Cash to close is the down payment plus estimated closing costs.</p>
            </>
          ) : (
            <>
              <div style={row}><span>Sale price</span><span>{money(salePrice)}</span></div>
              <div style={row}><span>Commission ({commissionPct}%)</span><span>-{money(commission)}</span></div>
              {sellerLines.map((l) => (
                <div key={l.label} style={row}><span>{l.label}</span><span>-{money(l.amount)}</span></div>
              ))}
              <div style={row}><span>Mortgage payoff</span><span>-{money(num(payoff))}</span></div>
              <div style={bigRow}><span>Estimated net to seller</span><span>{money(sellerNet)}</span></div>
            </>
          )}
        </div>

        <div className="no-print" style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => window.print()} style={btn}>Print or save as PDF</button>
        </div>
      </div>
    </div>
  );
}
